import type { VendingSalesFormValues, VendingSalesLine } from '../../../../types/vendingSalesFormTypes';

const DEFAULT_VENDING_BIN_COUNT = 24;

export const createEmptyVendingSalesLine = (bin: number | '' = ''): VendingSalesLine => ({
  bin,
  game: '',
  count: '',
  amount: '',
  quantity: '',
  value: '',
});

export const cloneVendingInventoryLine = (
  line: VendingSalesLine | null | undefined,
): VendingSalesLine => {
  const nextLine = { ...(line ?? createEmptyVendingSalesLine()) } as VendingSalesLine;
  const selectedPacks = line?.selected_packs;
  const packCandidates = line?.pack_candidates;

  if (Array.isArray(selectedPacks)) {
    nextLine.selected_packs = selectedPacks.map((pack) => ({ ...pack }));
  }

  if (Array.isArray(packCandidates)) {
    nextLine.pack_candidates = packCandidates.map((candidate) => ({ ...candidate }));
  }

  return nextLine;
};

const normalizeVendingBinCount = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return fallback;
  }

  return Math.min(parsed, 500);
};

const ensureVendingBinRows = (
  lines: VendingSalesLine[],
  binCount: number,
): VendingSalesLine[] => {
  const targetCount = Math.max(lines.length, normalizeVendingBinCount(binCount, DEFAULT_VENDING_BIN_COUNT), 1);
  return Array.from({ length: targetCount }, (_, index) => ({
    ...(lines[index] ?? createEmptyVendingSalesLine(index + 1)),
    bin: index + 1,
  }));
};

export const createVendingSalesFormDefaultValues = (
  formData: VendingSalesFormValues | null | undefined,
  reportType?: string,
): VendingSalesFormValues => {
  const isBinBasedReport = reportType === 'vending_inventory';
  const candidateLines = formData?.parsed_data?.final_json?.lines;
  const normalizedCandidateLines = Array.isArray(candidateLines)
    ? candidateLines.map((line) => {
      const nextLine = { ...line } as any;
      return nextLine as VendingSalesLine;
    })
    : [];

  const configuredBinCount = normalizeVendingBinCount(
    formData?.vending_bin_count
      ?? formData?.parsed_data?.vending_bin_count
      ?? formData?.parsed_data?.final_json?.vending_bin_count,
    normalizedCandidateLines.length || DEFAULT_VENDING_BIN_COUNT,
  );
  const lines = isBinBasedReport
    ? ensureVendingBinRows(normalizedCandidateLines, configuredBinCount)
    : normalizedCandidateLines;

  return {
    action: formData?.action ?? 'finalize',
    notes: formData?.notes ?? '',
    confidence_score: Number(formData?.confidence_score ?? 0),
    store_location_id: formData?.store_location_id ?? null,
    location_key: formData?.location_key ?? formData?.parsed_data?.location_key ?? formData?.parsed_data?.final_json?.location_key ?? '',
    shift_date: formData?.shift_date ?? '',
    shift_start_time: formData?.shift_start_time ?? '',
    parsed_data: {
      ...(formData?.parsed_data ?? {}),
      final_json: {
        ...(formData?.parsed_data?.final_json ?? {}),
        summary: formData?.parsed_data?.final_json?.summary ?? {},
        lines,
      },
    },
  };
};

export const buildVendingSalesJsonModeFormData = (currentFormValues: VendingSalesFormValues): VendingSalesFormValues => {
  const candidateLines = currentFormValues?.parsed_data?.final_json?.lines;
  const lines = Array.isArray(candidateLines) ? candidateLines.map((line) => ({ ...line })) : [];

  return {
    ...currentFormValues,
    location_key: currentFormValues?.location_key
      ?? currentFormValues?.parsed_data?.location_key
      ?? currentFormValues?.parsed_data?.final_json?.location_key
      ?? '',
    parsed_data: {
      ...(currentFormValues?.parsed_data ?? {}),
      final_json: {
        ...(currentFormValues?.parsed_data?.final_json ?? {}),
        summary: currentFormValues?.parsed_data?.final_json?.summary ?? {},
        lines,
      },
    },
  };
};
